// src/components/AssignContactsModal.js
import React, { useState, useEffect, useRef } from 'react';
import { FiSearch, FiX, FiCheck, FiPlus, FiChevronLeft } from 'react-icons/fi';

const AssignContactsModal = ({ isOpen, onClose, contacts, onSave, currentContactIds = [], onCreate }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedIds, setSelectedIds] = useState([]);
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'create'
    const [newContact, setNewContact] = useState({ name: '', title: '', email: '', phone: '' });
    const overlayMousedownTarget = useRef(null);
  
    useEffect(() => {
      if (isOpen) {
        setSelectedIds(currentContactIds);
        setSearchTerm(''); 
        setViewMode('list');
        setNewContact({ name: '', title: '', email: '', phone: '' });
      }
    }, [isOpen, currentContactIds]);

    // ✅ NEW: Close on Escape Key
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isOpen) {
                e.preventDefault();
                onClose();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);
    
    if (!isOpen) return null;
  
    const filteredContacts = contacts.filter(contact =>
      (contact.name?.toLowerCase() || '').includes(searchTerm.toLowerCase())
    );
    
    const handleToggleSelection = (contactId) => {
      setSelectedIds(prev =>
        prev.includes(contactId) ? prev.filter(id => id !== contactId) : [...prev, contactId]
      );
    };
  
    const handleSave = () => {
      onSave(selectedIds);
      onClose();
    };

    const handleCreateChange = (e) => {
        const { name, value } = e.target;
        setNewContact(prev => ({ ...prev, [name]: value }));
    };

    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        if (!newContact.name.trim()) return;

        if (onCreate) {
            await onCreate(newContact);
            setViewMode('list');
            setNewContact({ name: '', title: '', email: '', phone: '' });
            setSearchTerm('');
        }
    };
    
    const handleMouseDown = (e) => {
      overlayMousedownTarget.current = e.target;
    };
  
    const handleMouseUp = (e) => {
      if (overlayMousedownTarget.current === e.target && e.target === e.currentTarget) {
        onClose();
      }
      overlayMousedownTarget.current = null;
    };
  
    return (
      <div 
        className="modal-overlay" 
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
      >
        <div className="modal-content modal-content-slim">
          {viewMode === 'list' ? (
            <>
                <div className="modal-header">
                    <h3 className="modal-title">Assign Contacts</h3>
                    <button onClick={onClose} className="modal-close-btn"><FiX /></button>
                </div>
                <div className="modal-body">
                    <div className="search-input-wrapper modal-search-wrapper">
                        <FiSearch className="search-icon" />
                        <input
                            type="search"
                            placeholder="Search by name..."
                            className="search-input"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            autoFocus
                        />
                    </div>

                    {onCreate && (
                        <button 
                            className="btn btn-secondary btn-block" 
                            style={{ marginBottom: '1rem', borderStyle: 'dashed' }}
                            onClick={() => setViewMode('create')}
                        >
                            <FiPlus className="btn-icon" /> Create New Contact
                        </button>
                    )}
        
                    <div className="selection-list multi-select-list">
                        {filteredContacts.map(contact => {
                            const isSelected = selectedIds.includes(contact._id);
                            return (
                                <div
                                    key={contact._id}
                                    className={`selection-list-item ${isSelected ? 'selected' : ''}`}
                                    onClick={() => handleToggleSelection(contact._id)}
                                >
                                    <div className="item-main-info">
                                        <p className="item-title">{contact.name}</p>
                                        <p className="item-subtitle">{contact.title}</p>
                                    </div>
                                    <div className="item-meta-info">
                                        {isSelected && <FiCheck className="selection-checkmark" />}
                                    </div>
                                </div>
                            );
                        })}
                        {filteredContacts.length === 0 && (
                            <div className="selection-list-empty">
                                <p>No contacts found.</p>
                            </div>
                        )}
                    </div>
                </div>
                <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
                    <button type="button" className="btn btn-primary" onClick={handleSave}>
                        Save Assignments ({selectedIds.length})
                    </button>
                </div>
            </>
          ) : (
            <>
                <div className="modal-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <button onClick={() => setViewMode('list')} className="btn-icon-only"><FiChevronLeft /></button>
                        <h3 className="modal-title">New Contact</h3>
                    </div>
                    <button onClick={onClose} className="modal-close-btn"><FiX /></button>
                </div>
                <form onSubmit={handleCreateSubmit} className="modal-form">
                    <div className="modal-body">
                        <div className="form-group">
                            <label htmlFor="new-name">Name *</label>
                            <input type="text" id="new-name" name="name" className="form-input" value={newContact.name} onChange={handleCreateChange} required autoFocus />
                        </div>
                        <div className="form-group">
                            <label htmlFor="new-title">Title</label>
                            <input type="text" id="new-title" name="title" className="form-input" value={newContact.title} onChange={handleCreateChange} placeholder="e.g. Property Manager" />
                        </div>
                        <div className="form-group">
                            <label htmlFor="new-phone">Phone</label>
                            <input type="tel" id="new-phone" name="phone" className="form-input" value={newContact.phone} onChange={handleCreateChange} placeholder="(555) 555-5555" />
                        </div>
                        <div className="form-group">
                            <label htmlFor="new-email">Email</label>
                            <input type="email" id="new-email" name="email" className="form-input" value={newContact.email} onChange={handleCreateChange} placeholder="name@example.com" />
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={() => setViewMode('list')}>Back</button>
                        <button type="submit" className="btn btn-primary">Create & Select</button>
                    </div>
                </form>
            </>
          )}
        </div>
      </div>
    );
};

export default AssignContactsModal;